/**
 * LabelPreviewRenderer
 *
 * Renders an HTML preview of a ZPL label using the element array
 * produced by ZPLLabelBuilder. Used in both the print wizard and
 * the label template designer.
 */
import { Box, Typography } from '@mui/material';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import type { LabelElement } from '../../types/printing';

interface LabelPreviewRendererProps {
  elements: LabelElement[];
  widthDots: number;
  heightDots: number;
  scale?: number;
}

function QRPreview({ data, size }: { data: string; size: number }) {
  const [src, setSrc] = useState<string>('');

  useEffect(() => {
    QRCode.toDataURL(data, { width: size, margin: 1, errorCorrectionLevel: 'M' })
      .then(setSrc)
      .catch(() => setSrc(''));
  }, [data, size]);

  if (!src) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          border: '1px dashed #999',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          QR
        </Typography>
      </Box>
    );
  }

  return <img src={src} alt="QR Code" style={{ width: size, height: size }} />;
}

export function LabelPreviewRenderer({
  elements,
  widthDots,
  heightDots,
  scale = 0.5,
}: LabelPreviewRendererProps) {
  const renderElement = (element: LabelElement, index: number) => {
    switch (element.type) {
      case 'text':
        return (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              left: element.x * scale,
              top: element.y * scale,
              fontSize: element.fontSize * scale,
              fontFamily: 'monospace',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              transform: element.rotation ? `rotate(${element.rotation}deg)` : undefined,
              transformOrigin: 'top left',
            }}
          >
            {element.text}
          </Box>
        );

      case 'fieldblock':
        return (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              left: element.x * scale,
              top: element.y * scale,
              width: element.width * scale,
              fontSize: element.fontSize * scale,
              fontFamily: 'monospace',
              textAlign:
                element.alignment === 'C' ? 'center' : element.alignment === 'R' ? 'right' : 'left',
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}
          >
            {element.data}
          </Box>
        );

      case 'qrcode':
        return (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              left: element.x * scale,
              top: element.y * scale,
            }}
          >
            <QRPreview data={element.data} size={element.magnificationFactor * 21 * scale} />
          </Box>
        );

      case 'barcode':
        return (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              left: element.x * scale,
              top: element.y * scale,
              height: element.height * scale,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                height: element.height * scale * 0.7,
                width: Math.max(80, element.data.length * 8) * scale,
                background:
                  'repeating-linear-gradient(90deg, #000 0px, #000 2px, #fff 2px, #fff 4px)',
              }}
            />
            {element.humanReadable === 'Y' && (
              <Typography
                variant="caption"
                sx={{ fontSize: 8 * scale, fontFamily: 'monospace', mt: 0.25 }}
              >
                {element.data}
              </Typography>
            )}
          </Box>
        );

      case 'box':
        return (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              left: element.x * scale,
              top: element.y * scale,
              width: element.width * scale,
              height: element.height * scale,
              border: `${Math.max(1, element.borderThickness * scale)}px solid ${
                element.borderColor === 'W' ? '#fff' : '#000'
              }`,
              borderRadius: element.cornerRounding * scale,
              boxSizing: 'border-box',
            }}
          />
        );

      case 'line':
        return (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              left: element.x * scale,
              top: element.y * scale,
              width: element.width * scale,
              height: Math.max(1, element.thickness * scale),
              backgroundColor: '#000',
            }}
          />
        );

      case 'inverse-box':
        return (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              left: element.x * scale,
              top: element.y * scale,
              width: element.width * scale,
              height: element.height * scale,
              backgroundColor: '#000',
              borderRadius: element.cornerRounding * scale,
            }}
          />
        );

      case 'image':
        return (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              left: element.x * scale,
              top: element.y * scale,
              width: 40 * scale,
              height: 40 * scale,
              border: '1px dashed #999',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="caption" sx={{ fontSize: 8 * scale }}>
              IMG
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: widthDots * scale,
        height: heightDots * scale,
        border: '1px solid #ccc',
        backgroundColor: '#fff',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {elements.map(renderElement)}
    </Box>
  );
}
